import { Fragment, ReactNode, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'

interface ModalProps {
  title: string
  message: string
  onConfirm: () => void
  leftButtonLabel?: string
  confirmButtonLabel?: string
  icon?: ReactNode
  canConfirm?: boolean
  children?: ReactNode
  open?: boolean
  handleClose: (open: boolean) => void
}

export default function Modal({
  title,
  message,
  onConfirm,
  leftButtonLabel = 'Cancel',
  confirmButtonLabel: rightButtonLabel = 'Confirm',
  canConfirm = true,
  open = true,
  handleClose: setOpen,
  icon,
  children,
}: ModalProps) {
  const handleConfirm = (e) => {
    e.preventDefault()
    onConfirm()
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                <div>
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full sm:h-10 sm:w-10">
                    {icon}
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      {title}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">{message}</p>
                    </div>
                  </div>
                </div>
                <form onSubmit={handleConfirm} className="mt-5 sm:mt-6">
                  {children}
                  <div className="mt-5 sm:mt-6">
                    <button
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                      onClick={handleConfirm}
                      disabled={!canConfirm}
                    >
                      {rightButtonLabel}
                    </button>
                    <button
                      className="mt-4 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                      onClick={() => setOpen(false)}
                    >
                      {leftButtonLabel}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
